import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ShellSectionHeading({ icon: Icon, title, description, actions }: { icon: LucideIcon; title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-1">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary" aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <h2 className="text-base font-semibold first-letter:uppercase">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ShellPanel({
  icon: Icon,
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('rounded-2xl border bg-card shadow-sm', className)}>
      <header className="flex flex-wrap items-center gap-3 border-b px-5 py-3.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary" aria-hidden>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <h2 className="text-sm font-semibold first-letter:uppercase">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <div className={cn('p-5', bodyClassName)}>{children}</div>
    </section>
  );
}

export function ShellEmpty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed bg-muted/20 px-3 py-2.5 text-sm text-muted-foreground">
      <span>{children}</span>
      {action}
    </div>
  );
}

export function ShellSummaryCard({
  icon: Icon,
  title,
  onOpen,
  aside,
  children,
  className,
}: {
  icon: LucideIcon;
  title: string;
  onOpen?: () => void;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const header = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary" aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</span>
      {aside}
    </>
  );
  return (
    <section className={cn('flex flex-col rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md', className)}>
      {onOpen ? (
        <button type="button" onClick={onOpen} className="flex items-center gap-2.5 rounded-t-2xl border-b px-4 py-3 text-start focus-ring">
          {header}
        </button>
      ) : (
        <div className="flex items-center gap-2.5 border-b px-4 py-3">{header}</div>
      )}
      <div className="flex-1 px-4 py-3 text-sm">{children}</div>
    </section>
  );
}
